import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAuth } from "../../context/AuthContext";
import {
  X, Send, Plus, Sparkles, MessageSquare, Trash2, BookOpen, ShieldAlert, Loader2,
  Paperclip, FileText, Image as ImageIcon, Download, Lock, ArrowRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const NAVY = "#032866";
const YELLOW = "#FFDA00";

async function apiFetch(path, opts = {}) {
  const token = localStorage.getItem("token");
  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API}/api${path}`, { ...opts, headers });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json();
}

async function streamChat({ conversationId, content, command, patientId, onEvent, signal }) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${API}/api/avi/chat`, {
    method: "POST",
    signal,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ conversation_id: conversationId, content, command, patient_id: patientId || undefined }),
  });
  if (!res.ok) {
    let msg = `${res.status}`;
    try { msg = (await res.json()).detail || msg; } catch { /* ignore */ }
    onEvent({ type: "error", message: msg });
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const line = p.trim();
      if (!line.startsWith("data:")) continue;
      const payload = line.slice(5).trim();
      try {
        onEvent(JSON.parse(payload));
      } catch { /* ignore malformed */ }
    }
  }
}

// ── Citation chip
function CitationChip({ c, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen?.(c)}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 transition-colors"
      title={c.document_title}
      data-testid={`avi-citation-${c.citation_label}`}
    >
      <BookOpen className="w-3 h-3" />
      {c.citation_label}
    </button>
  );
}

// ── Inline chart-citation chip (clickable "PCST · 8/20/26" style marker that
// jumps into the client's file / opens the source document; hovering shows a
// quick peek of the source snippet)
function ChartCiteChip({ cite, label, onClick, onHover, onLeave }) {
  const isDoc = cite?.kind === "doc";
  const Icon = isDoc ? FileText : BookOpen;
  return (
    <button
      type="button"
      onClick={() => onClick?.(cite)}
      onMouseEnter={(e) => onHover?.(cite, e.currentTarget)}
      onMouseLeave={() => onLeave?.()}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded-md text-[10.5px] font-semibold align-baseline bg-[color:var(--avi-navy,#032866)]/5 hover:bg-[color:var(--avi-navy,#032866)]/12 text-[color:var(--avi-navy,#032866)] border border-[color:var(--avi-navy,#032866)]/20 transition-colors no-underline"
      style={{ color: NAVY }}
      title={`Open ${cite?.label || label} in the client's file`}
      data-testid={`avi-chart-cite-${cite?.token || label}`}
    >
      <Icon className="w-3 h-3 shrink-0" />
      {cite?.label || label}
    </button>
  );
}

// ── Inline "unverified" flag — Avi asserted a client fact it could not ground
// in the chart, so the operator should double-check it.
function VerifyFlag() {
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded-md text-[10.5px] font-semibold align-baseline bg-amber-100 text-amber-800 border border-amber-300 no-underline"
      title="Avi could not find this in the client's chart — please double-check it."
      data-testid="avi-unverified-flag"
    >
      <ShieldAlert className="w-3 h-3 shrink-0" />
      verify
    </span>
  );
}

// Rewrite Avi's inline [[cite:TOKEN]] / [[unverified]] markers into markdown
// links we intercept in ReactMarkdown. Cite tokens without a registry entry are
// stripped so raw markers never leak into the rendered answer.
function injectCiteMarkers(content, citeMap) {
  if (!content) return content;
  let out = content.replace(/\s*\[\[cite:([A-Za-z0-9_]+)\]\]/g, (_m, tok) => {
    const c = citeMap[tok];
    if (!c) return "";
    return ` [${c.label || tok}](avidacite:${tok})`;
  });
  out = out.replace(/\s*\[\[unverified\]\]/gi, () => " [verify](avidaverify:1)");
  return out;
}

// Convert raw HTTP-style errors ("404: {"detail":"Not Found"}") into
// something a coordinator can actually act on. We keep the raw text under
// a "details" line for support/debug.
function humanizeError(raw) {
  const text = String(raw || "");
  const m = text.match(/^(\d{3}):/);
  const status = m ? m[1] : "";
  let friendly;
  switch (status) {
    case "401":
      friendly = "Your session expired. Sign back in and re-open Ask Avi.";
      break;
    case "403":
      friendly = "You don't have access to this Ask Avi action. Ask your admin for the avi.chat permission.";
      break;
    case "404":
      friendly = "That chat or resource isn't available anymore — it may have been retired. Start a new chat and try again.";
      break;
    case "413":
      friendly = "That upload was too large. Try a smaller file or split the PDF.";
      break;
    case "429":
      friendly = "Ask Avi is rate-limited right now. Wait a few seconds and try again.";
      break;
    case "500":
    case "502":
    case "503":
    case "504":
      friendly = "Ask Avi is temporarily unreachable — the service may be restarting. Retry in a few seconds.";
      break;
    default:
      friendly = text.length < 200 ? text : "Ask Avi hit an unexpected error. Try again.";
  }
  return { friendly, raw: text };
}

// ── One message bubble
function MessageBubble({ msg, onOpenCitation, onOpenChartCite, onSwitchClient }) {
  const isUser = msg.role === "user";
  const isError = msg.role === "error";
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  const [hoverCite, setHoverCite] = useState(null);   // { cite, top, left }
  const onCiteHover = (cite, el) => {
    if (!cite) return;
    const r = el.getBoundingClientRect();
    const left = Math.min(Math.max(8, r.left), (typeof window !== "undefined" ? window.innerWidth : 1000) - 340);
    setHoverCite({ cite, top: r.bottom + 6, left });
  };
  const onCiteLeave = () => setHoverCite(null);

  const citeMap = useMemo(() => {
    const map = {};
    (msg.chart_citations || []).forEach((c) => { if (c?.token) map[c.token] = c; });
    return map;
  }, [msg.chart_citations]);
  const renderedContent = useMemo(
    () => (isUser || isError ? msg.content : injectCiteMarkers(msg.content, citeMap)),
    [msg.content, citeMap, isUser, isError],
  );
  const unverifiedCount = useMemo(
    () => (isUser || isError ? 0 : (msg.content?.match(/\[\[unverified\]\]/gi) || []).length),
    [msg.content, isUser, isError],
  );
  const citedCount = useMemo(
    () => (isUser || isError ? 0 : (msg.content?.match(/\[\[cite:/g) || []).length),
    [msg.content, isUser, isError],
  );
  const showConfidence = !isUser && !isError && !msg.streaming
    && ((msg.chart_citations || []).length > 0 || unverifiedCount > 0);
  const mdComponents = useMemo(() => ({
    a: ({ href, children }) => {
      if (href && href.startsWith("avidacite:")) {
        const tok = href.slice("avidacite:".length);
        return <ChartCiteChip cite={citeMap[tok]} label={children} onClick={onOpenChartCite} onHover={onCiteHover} onLeave={onCiteLeave} />;
      }
      if (href && href.startsWith("avidaverify:")) {
        return <VerifyFlag />;
      }
      return <a href={href} target="_blank" rel="noreferrer">{children}</a>;
    },
    table: ({ children }) => (
      <div className="my-2 -mx-1 overflow-x-auto" data-testid="avi-report-table">
        <table className="w-full border-collapse text-[11px] leading-tight">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-[#032866] text-white">{children}</thead>,
    th: ({ children }) => (
      <th className="border border-slate-200 px-2 py-1 text-left font-semibold align-top">{children}</th>
    ),
    td: ({ children }) => (
      <td className="border border-slate-200 px-2 py-1 align-top odd:bg-white even:bg-slate-50">{children}</td>
    ),
    blockquote: ({ children }) => (
      <blockquote className="my-1.5 border-l-4 border-[#FFDA00] bg-violet-50/60 px-3 py-1.5 rounded-r-md text-slate-800 not-italic">
        {children}
      </blockquote>
    ),
  }), [citeMap, onOpenChartCite]);

  // "Export as PDF" — fires only for finished assistant messages that were
  // persisted server-side (they have a stable UUID). Streaming bubbles use
  // a client-side "a-<timestamp>" id and can't be exported until the stream
  // completes and the id is swapped.
  const canExport = !isUser && !isError && !msg.streaming
                    && msg.id && !String(msg.id).startsWith("a-");

  async function handleExport() {
    if (exporting || !canExport) return;
    setExporting(true); setExportError("");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/api/avi/messages/${msg.id}/export-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(t.slice(0, 200) || `Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Prefer the server's self-identifying filename (Last_First_AV#_Report_Date)
      // from Content-Disposition; fall back to a sensible default.
      let fname = "avida-ask-avi-draft.pdf";
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      if (m && m[1]) fname = decodeURIComponent(m[1]);
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      setExportError((e && e.message) || "Export failed");
    } finally {
      setExporting(false);
    }
  }

  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"} px-4 py-2`}
      data-testid={`avi-msg-${msg.role}`}
    >
      <div
        className={`max-w-[92%] rounded-2xl px-4 py-3 shadow-sm ${
          isUser
            ? "bg-[color:var(--avi-navy,#032866)] text-white rounded-br-sm"
            : isError
              ? "bg-red-50 text-red-800 border border-red-200 rounded-bl-sm"
              : "bg-white text-slate-900 border border-slate-200 rounded-bl-sm"
        }`}
        style={isUser ? { background: NAVY } : {}}
      >
        {isUser || isError ? (
          <div className="whitespace-pre-wrap text-sm leading-relaxed">{msg.content}</div>
        ) : (
          <div className="prose prose-sm max-w-none prose-slate prose-p:my-2 prose-headings:mt-3 prose-headings:mb-1 prose-li:my-0.5 prose-strong:text-slate-900">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={mdComponents}
              urlTransform={(url) => (url && (url.startsWith("avidacite:") || url.startsWith("avidaverify:")) ? url : defaultUrlTransform(url))}
            >
              {renderedContent || (msg.streaming ? "…" : "")}
            </ReactMarkdown>
          </div>
        )}
        {showConfidence && (
          <div
            className={`mt-3 pt-2 border-t flex items-start gap-1.5 text-[11px] font-semibold ${unverifiedCount > 0 ? "border-amber-200 text-amber-700" : "border-slate-200 text-emerald-700"}`}
            data-testid="avi-confidence-footer"
          >
            {unverifiedCount > 0 ? (
              <>
                <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>{unverifiedCount} statement{unverifiedCount > 1 ? "s" : ""} couldn't be backed by the chart — double-check the flagged {unverifiedCount > 1 ? "items" : "item"}.</span>
              </>
            ) : (
              <>
                <BookOpen className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>Every client fact in this answer is backed by a chart citation.</span>
              </>
            )}
          </div>
        )}
        {!isUser && !isError && msg.switch_suggestion?.patient_id && (
          <div className="mt-3 pt-2 border-t border-slate-200">
            <button
              type="button"
              onClick={() => onSwitchClient?.(msg.switch_suggestion.patient_id)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold transition-all hover:shadow-sm"
              style={{ background: NAVY, color: "#fff" }}
              data-testid="avi-switch-client-btn"
            >
              <ArrowRight className="w-3.5 h-3.5" />
              Open {msg.switch_suggestion.name}'s file
            </button>
          </div>
        )}
        {!isUser && msg.citations?.length > 0 && (
          <div className="mt-3 pt-2 border-t border-slate-200 flex flex-wrap gap-1.5">
            {msg.citations.map((c, i) => (
              <CitationChip key={`${c.chunk_id || i}`} c={c} onOpen={onOpenCitation} />
            ))}
          </div>
        )}
        {canExport && (
          <div className="mt-3 pt-2 border-t border-slate-100 flex items-center gap-2">
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[11px] font-bold transition-all hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: NAVY, background: YELLOW, color: NAVY }}
              data-testid={`avi-export-pdf-${msg.id}`}
              title="Download this reply as an Avida-branded PDF"
            >
              {exporting ? (
                <><Loader2 className="w-3 h-3 animate-spin" /> Building PDF…</>
              ) : (
                <><Download className="w-3 h-3" /> Export as PDF</>
              )}
            </button>
            {exportError && (
              <span className="text-[10px] text-rose-600" data-testid={`avi-export-error-${msg.id}`}>
                {exportError}
              </span>
            )}
          </div>
        )}
        {hoverCite && (
          <div
            className="fixed z-[95] w-80 max-w-[80vw] rounded-lg border border-slate-200 bg-white shadow-xl p-3 text-left pointer-events-none"
            style={{ top: hoverCite.top, left: hoverCite.left }}
            data-testid="avi-cite-hover"
          >
            <div className="flex items-center gap-1.5 mb-1">
              {hoverCite.cite.kind === "doc"
                ? <FileText className="w-3.5 h-3.5 shrink-0" style={{ color: NAVY }} />
                : <BookOpen className="w-3.5 h-3.5 shrink-0" style={{ color: NAVY }} />}
              <span className="text-[11.5px] font-bold truncate" style={{ color: NAVY }}>{hoverCite.cite.label}</span>
            </div>
            <p className="text-[11px] text-slate-600 leading-snug" style={{ maxHeight: 92, overflow: "hidden" }}>
              {hoverCite.cite.snippet || "Open to view this source in the client's file."}
            </p>
            <p className="text-[10px] text-slate-400 mt-1.5">Click to open in the client's file</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Conversation history sidebar (left pane inside the drawer)
function ConversationList({ convos, activeId, onPick, onNew }) {
  return (
    <div className="w-56 border-r border-slate-200 bg-slate-50 flex flex-col shrink-0" data-testid="avi-convo-list">
      <button
        type="button"
        onClick={onNew}
        className="m-3 flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold shadow-sm hover:shadow-md transition-all"
        style={{ background: YELLOW, color: NAVY, border: `1px solid ${NAVY}` }}
        data-testid="avi-new-chat"
      >
        <Plus className="w-4 h-4" strokeWidth={2.5} />
        New chat
      </button>
      <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-1">
        {convos.length === 0 ? (
          <div className="text-xs text-slate-400 px-2 py-4 text-center">No conversations yet.</div>
        ) : convos.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onPick(c.id)}
            className={`w-full text-left px-2.5 py-2 rounded-lg text-xs transition-colors ${
              c.id === activeId
                ? "bg-white text-slate-900 shadow-sm border border-slate-200"
                : "text-slate-600 hover:bg-white/70"
            }`}
            data-testid={`avi-convo-${c.id}`}
          >
            <div className="flex items-center gap-1.5 truncate">
              <MessageSquare className="w-3 h-3 shrink-0 text-slate-400" />
              <span className="truncate font-medium">{c.title || "New chat"}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Main drawer
export default function AviChatDrawer({ open, onClose, initialPrompt = "", initialPatientId = null, initialPatientName = "", initialCommand = "" }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userRole = user?.role || "";
  const isManager = ["super_admin", "admin", "owner"].includes(userRole);
  const [cfg, setCfg] = useState(null);
  const [convos, setConvos] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activePatientId, setActivePatientId] = useState(null);  // client in context (from a client file)
  const [activePatientName, setActivePatientName] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [selectedCommand, setSelectedCommand] = useState("");
  const [error, setError] = useState("");
  const [attachments, setAttachments] = useState([]);   // list from /attachments endpoint
  const [uploading, setUploading] = useState(false);
  const bodyRef = useRef(null);
  const abortRef = useRef(null);
  const fileInputRef = useRef(null);

  // Seed the composer whenever the drawer is opened with a starter prompt
  // (from the /tools Ask Avi chip buttons, sidebar quick actions, etc.).
  // We overwrite whatever's currently in the composer so ops can quickly
  // switch between chip prompts without stale text.
  useEffect(() => {
    if (open && initialPrompt) setInput(initialPrompt);
  }, [open, initialPrompt]);

  // When Avi is opened from a client's file, scope this session to that client
  // so Avi auto-loads their EHR / chart — no need to name them or attach docs.
  useEffect(() => {
    if (open) {
      setActivePatientId(initialPatientId || null);
      setActivePatientName(initialPatientName || "");
      setSelectedCommand(initialCommand || "");
    }
  }, [open, initialPatientId, initialPatientName, initialCommand]);

  // Load config + conversations on open
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [cfgRes, convRes] = await Promise.all([
          apiFetch("/avi/config"),
          apiFetch("/avi/conversations"),
        ]);
        if (cancelled) return;
        setCfg(cfgRes);
        setConvos(convRes.conversations || []);
        if (initialPatientId) {
          // Opened from a client's file → start a CLEAN client-scoped chat
          // instead of resuming an unrelated recent one, so the session stays
          // locked to this one client (cross-client guardrail).
          setActiveId(null);
          setMessages([]);
          setAttachments([]);
          setSelectedCommand(initialCommand || "");
        } else if (convRes.conversations?.length) {
          // Auto-open the most recent conversation, or create one
          await loadConversation(convRes.conversations[0].id);
        }
      } catch (e) {
        if (cancelled) return;
        // 403 → user not permitted; just show a friendly message
        if (String(e.message).startsWith("403")) {
          setError("You don't have access to Ask Avi. Ask your admin for the avi.chat permission.");
        } else {
          setError(String(e.message).slice(0, 200));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Jump from an inline chart citation straight into the client's file and
  // open the exact document/section it came from (uses the app's own
  // permission-checked viewer).
  function openChartCite(cite) {
    if (!cite || !activePatientId) return;
    const pid = activePatientId;
    if (cite.kind === "doc" && cite.doc_id) {
      navigate(`/healthcare/patients/${pid}?tab=documents&openDoc=${encodeURIComponent(cite.doc_id)}`);
    } else if (cite.kind === "section") {
      const s = cite.section;
      if (s === "pcst") navigate(`/healthcare/patients/${pid}/pcst`);
      else if (s === "poc") navigate(`/healthcare/patients/${pid}/cms485`);
      else if (s === "supervisory") navigate(`/healthcare/patients/${pid}?tab=documents&doctab=supervisory_visits`);
      else navigate(`/healthcare/patients/${pid}?tab=documents`);
    } else {
      navigate(`/healthcare/patients/${pid}`);
    }
    onClose?.();
  }

  // "Open {name}'s file" — the operator asked about a different client while
  // pinned; take them to that client's file (Avi will re-pin there).
  function switchClient(patientId) {
    if (!patientId) return;
    navigate(`/healthcare/patients/${patientId}`);
    onClose?.();
  }

  async function loadConversation(cid) {
    setActiveId(cid);
    setMessages([]);
    setSelectedCommand("");
    setAttachments([]);
    try {
      const [msgs, atts] = await Promise.all([
        apiFetch(`/avi/conversations/${cid}/messages`),
        apiFetch(`/avi/conversations/${cid}/attachments`),
      ]);
      setMessages(msgs.messages || []);
      setAttachments(atts.attachments || []);
    } catch (e) {
      setError(String(e.message).slice(0, 200));
    }
  }

  async function handleUploadFiles(files) {
    const cid = await ensureConversation();
    setUploading(true);
    setError("");
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const token = localStorage.getItem("token");
        const res = await fetch(`${API}/api/avi/conversations/${cid}/attachments`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        });
        if (!res.ok) {
          const detail = (await res.json().catch(() => ({}))).detail || `${res.status}`;
          throw new Error(detail);
        }
        const rec = await res.json();
        setAttachments((prev) => [...prev, rec]);
      }
    } catch (e) {
      setError(String(e.message).slice(0, 240));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleRemoveAttachment(aid) {
    if (!activeId) return;
    try {
      await apiFetch(`/avi/conversations/${activeId}/attachments/${aid}`, { method: "DELETE" });
      setAttachments((prev) => prev.filter((a) => a.id !== aid));
    } catch (e) {
      setError(String(e.message).slice(0, 200));
    }
  }

  async function handleNewChat() {
    try {
      const c = await apiFetch("/avi/conversations", { method: "POST", body: JSON.stringify({}) });
      setConvos((prev) => [c, ...prev]);
      setActiveId(c.id);
      setMessages([]);
      setSelectedCommand("");
      setAttachments([]);
    } catch (e) {
      setError(String(e.message).slice(0, 200));
    }
  }

  async function ensureConversation() {
    if (activeId) return activeId;
    const c = await apiFetch("/avi/conversations", { method: "POST", body: JSON.stringify({}) });
    setConvos((prev) => [c, ...prev]);
    setActiveId(c.id);
    return c.id;
  }

  async function handleSend(overrideContent) {
    const content = (typeof overrideContent === "string" ? overrideContent : input).trim();
    if (!content || sending) return;
    setError("");
    setInput("");
    setSending(true);
    const cid = await ensureConversation();

    // Optimistic user + streaming assistant bubbles
    const userMsg = { id: `u-${Date.now()}`, role: "user", content };
    const assistantMsg = { id: `a-${Date.now()}`, role: "assistant", content: "", citations: [], chart_citations: [], switch_suggestion: null, streaming: true };
    setMessages((m) => [...m, userMsg, assistantMsg]);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamChat({
        conversationId: cid,
        content,
        command: selectedCommand || undefined,
        patientId: activePatientId,
        signal: controller.signal,
        onEvent: (ev) => {
          setMessages((m) => {
            const last = m[m.length - 1];
            if (!last || last.id !== assistantMsg.id) return m;
            const updated = { ...last };
            if (ev.type === "delta") {
              updated.content = (last.content || "") + (ev.text || "");
            } else if (ev.type === "meta") {
              updated.citations = ev.corpus_chunks || [];
              updated.model_version = ev.model;
            } else if (ev.type === "done") {
              updated.citations = ev.citations?.length ? ev.citations : (last.citations || []);
              updated.chart_citations = ev.chart_citations || [];
              updated.switch_suggestion = ev.switch_suggestion || null;
              updated.streaming = false;
            } else if (ev.type === "error") {
              updated.role = "error";
              updated.content = ev.message || "Model call failed.";
              updated.streaming = false;
            } else {
              return m;
            }
            return [...m.slice(0, -1), updated];
          });
        },
      });
    } catch (e) {
      setError(String(e.message).slice(0, 200));
    } finally {
      setSending(false);
      setSelectedCommand("");
      abortRef.current = null;
      // Re-fetch convo list so the auto-titled convo shows up
      try {
        const r = await apiFetch("/avi/conversations");
        setConvos(r.conversations || []);
      } catch { /* ignore */ }
      // Re-fetch this conversation's messages so the optimistic
      // "a-{timestamp}" bubble is replaced by the server-persisted
      // message with its stable UUID. This is what enables Export-as-PDF
      // for the reply we just finished streaming.
      try {
        const r2 = await apiFetch(`/avi/conversations/${cid}/messages`);
        if (r2 && Array.isArray(r2.messages)) {
          setMessages(r2.messages);
        }
      } catch { /* ignore — optimistic bubbles will just miss the export button */ }
    }
  }

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages]);

  const cmdList = useMemo(() => cfg?.commands || [], [cfg]);
  const branding = cfg?.branding || {};
  const welcome = branding.welcome_copy;

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9995] flex justify-end"
      role="dialog"
      aria-modal="true"
      data-testid="avi-drawer"
    >
      {/* backdrop */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close Ask Avi"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]"
        data-testid="avi-backdrop"
      />
      {/* drawer */}
      <div
        className="relative h-full w-full sm:w-[720px] lg:w-[900px] flex bg-white shadow-2xl animate-[avidrawer_240ms_ease-out]"
        style={{ animationName: "avidrawer" }}
      >
        {/* Left: conversations */}
        <ConversationList
          convos={convos}
          activeId={activeId}
          onPick={(cid) => { setActivePatientId(null); setActivePatientName(""); loadConversation(cid); }}
          onNew={handleNewChat}
        />

        {/* Right: chat */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3 border-b border-slate-200"
            style={{ background: NAVY, color: "white" }}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                style={{ background: YELLOW, color: NAVY }}
              >
                <Sparkles className="w-4 h-4" strokeWidth={2.5} />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold leading-tight" data-testid="avi-header-name">
                  {branding.product_name || "Ask Avi"}
                </div>
                <div className="text-[11px] text-white/70 truncate">
                  {cfg?.model || "Wisconsin PCS compliance"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate("/admin/avi/corpus")}
                className="text-[11px] px-2 py-1 rounded hover:bg-white/10 transition-colors"
                title="Manage knowledge base"
                data-testid="avi-open-corpus"
              >
                Corpus
              </button>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="p-1.5 rounded hover:bg-white/10 transition-colors"
                data-testid="avi-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Cross-client guardrail — visible lock when scoped to a client file */}
          {activePatientId && (
            <div
              className="flex items-center gap-2 px-4 py-2 text-[11.5px] font-semibold border-b border-amber-200"
              style={{ background: "#FFF7DC", color: "#7a5b00" }}
              data-testid="avi-client-scope-badge"
            >
              <Lock className="w-3.5 h-3.5 shrink-0" strokeWidth={2.5} />
              <span className="truncate">
                Locked to {activePatientName ? `${activePatientName}'s` : "this client's"} chart — Avi answers only about this client. Open another client's file to ask about them.
              </span>
            </div>
          )}

          {/* Body */}
          <div
            ref={bodyRef}
            className="flex-1 overflow-y-auto py-2"
            style={{ background: "#F8FAFC" }}
            data-testid="avi-body"
          >
            {error && (
              <div
                className="mx-4 my-3 px-3 py-2 rounded bg-red-50 border border-red-200 text-red-800 text-xs flex items-start gap-2"
                data-testid="avi-error"
              >
                <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="font-semibold">{humanizeError(error).friendly}</p>
                  {humanizeError(error).raw !== humanizeError(error).friendly && (
                    <p className="text-[10px] text-red-600/80 mt-0.5 truncate" title={humanizeError(error).raw}>
                      Details: {humanizeError(error).raw.slice(0, 140)}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => setError("")}
                    className="text-[10px] font-bold uppercase tracking-[0.14em] mt-1 text-red-700 hover:text-red-900"
                    data-testid="avi-error-dismiss"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}
            {messages.length === 0 && !error && (
              <div className="px-6 pt-8 pb-4 text-center" data-testid="avi-welcome">
                <div
                  className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-3"
                  style={{ background: YELLOW, color: NAVY }}
                >
                  <Sparkles className="w-6 h-6" strokeWidth={2.5} />
                </div>
                <div className="text-lg font-bold text-slate-900 mb-1">
                  {branding.assistant_name ? `Hi, I'm ${branding.assistant_name}.` : "Hi, I'm Avi."}
                </div>
                {welcome && (
                  <p className="text-xs text-slate-600 leading-relaxed max-w-lg mx-auto">{welcome}</p>
                )}
                {cmdList.length > 0 && (
                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg mx-auto">
                    {cmdList.slice(0, 8).map((cmd) => (
                      <button
                        key={cmd.name}
                        type="button"
                        onClick={() => setSelectedCommand(cmd.name)}
                        className={`text-left rounded-lg border px-3 py-2 text-[11px] transition-all ${
                          selectedCommand === cmd.name
                            ? "border-[#032866] bg-white shadow-sm"
                            : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                        data-testid={`avi-cmd-${cmd.name}`}
                      >
                        <div className="font-semibold text-slate-900 mb-0.5">/{cmd.name}</div>
                        <div className="text-slate-500 leading-snug">{cmd.description}</div>
                      </button>
                    ))}
                  </div>
                )}
                {isManager && (
                  <div className="mt-4 max-w-lg mx-auto" data-testid="avi-quick-questions">
                    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-2">Quick questions</div>
                    <div className="flex flex-wrap gap-2 justify-center">
                      <button
                        type="button"
                        onClick={() => handleSend("Who hasn't submitted their timesheet yet?")}
                        className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] text-slate-700 hover:border-[#032866] hover:shadow-sm transition-all"
                        data-testid="avi-quickq-timesheets"
                      >
                        Who hasn't submitted their timesheet yet?
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {messages.map((m) => (
              <MessageBubble
                key={m.id}
                msg={m}
                onOpenCitation={() => { /* future: open corpus modal */ }}
                onOpenChartCite={openChartCite}
                onSwitchClient={switchClient}
              />
            ))}
            {sending && (
              <div className="px-6 py-1 text-[11px] text-slate-500 flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" /> Avi is thinking…
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-slate-200 bg-white p-3">
            {/* Attachment chips row */}
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="avi-attachment-list">
                {attachments.map((a) => {
                  const IconEl = (a.content_type || "").startsWith("image/") ? ImageIcon : FileText;
                  return (
                    <span
                      key={a.id}
                      className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-full bg-slate-100 border border-slate-200 text-[11px]"
                      data-testid={`avi-attachment-${a.id}`}
                      title={`${a.filename} · ${a.page_count} page${a.page_count === 1 ? "" : "s"}${a.ocr_used ? " · OCR" : ""}`}
                    >
                      <IconEl className="w-3 h-3 text-slate-500" />
                      <span className="max-w-[180px] truncate font-medium text-slate-700">{a.filename}</span>
                      <span className="text-[9px] text-slate-400">
                        {a.page_count}p{a.ocr_used ? " · OCR" : ""}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveAttachment(a.id)}
                        className="ml-1 w-4 h-4 rounded-full hover:bg-slate-300 flex items-center justify-center"
                        aria-label={`Remove ${a.filename}`}
                        data-testid={`avi-attachment-remove-${a.id}`}
                      >
                        <X className="w-2.5 h-2.5 text-slate-500" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            {selectedCommand && (
              <div className="flex items-center gap-2 mb-2 text-[11px]">
                <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-medium">
                  /{selectedCommand}
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedCommand("")}
                  className="text-slate-400 hover:text-slate-700"
                  data-testid="avi-clear-cmd"
                >
                  clear
                </button>
              </div>
            )}
            <div className="flex items-end gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,image/png,image/jpeg,image/jpg,image/webp,image/heic,image/heif"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length) handleUploadFiles(files);
                }}
                className="hidden"
                data-testid="avi-file-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || sending}
                className="rounded-lg p-2 border border-slate-300 hover:bg-slate-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Attach PDF or image"
                title="Attach PDF or image (max 10 MB — text/OCR extracted, 24h ephemeral)"
                data-testid="avi-attach"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
                ) : (
                  <Paperclip className="w-4 h-4 text-slate-600" />
                )}
              </button>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={selectedCommand
                  ? `Provide inputs for /${selectedCommand}…`
                  : attachments.length
                    ? "Ask about the attached document(s)…"
                    : "Ask about DHS 107.112, PCST scoring, PA documentation…"}
                rows={2}
                className="flex-1 resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#032866]/40"
                data-testid="avi-input"
                disabled={sending}
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={!input.trim() || sending}
                className="rounded-lg px-3 py-2 text-sm font-semibold shadow-sm hover:shadow-md transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: NAVY, color: "white" }}
                data-testid="avi-send"
                aria-label="Send"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
            <div className="mt-1.5 flex items-center justify-between text-[10px] text-slate-400">
              <span>
                Every answer cites its source. Draft outputs need human review.
                {attachments.length > 0 && " · Attachments auto-purge 24h after last message."}
              </span>
              {cfg?.phi_enabled === false ? (
                <span className="italic">PHI-safe mode off — de-identify inputs.</span>
              ) : (
                <span className="italic font-medium" style={{ color: "#059669" }} data-testid="avi-phi-badge">
                  PHI-safe mode ON · BAA-covered
                </span>
              )}
            </div>
          </div>
        </div>

        {/* enter animation keyframes */}
        <style>{`
          @keyframes avidrawer {
            from { transform: translateX(24px); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}</style>
      </div>
    </div>
  );
}

// Export a helper for opening from anywhere in the app
export function openAvi() {
  if (typeof window !== "undefined" && window.__avidaAvi) {
    window.__avidaAvi.open();
  }
}
